/**
 * Credential references in stored config text (website-backend#81).
 *
 * A gateway replaces each secret in a config with a reference before the config
 * leaves it: `{{credential:<id>@<version>|<form>}}`, or, where nothing could
 * keep the value, `{{credential:redacted|<form>}}`. The grammar is
 * vendor-profiles' `secrets::Reference`.
 */

export interface CredentialReference {
  /** The text as written. */
  raw: string;
  /** Null for a redacted reference. */
  id: string | null;
  version: number | null;
  /** How the device holds it: `plain`, `cisco7`, `cisco5:<salt>`, `raw`, … */
  form: string;
}

const REFERENCE = /\{\{credential:(?:([0-9a-fA-F-]{36})@(\d+)|redacted)\|([A-Za-z0-9./=$:+_-]+)\}\}/g;

/** `text` cut into plain runs and the references between them. */
export function splitReferences(text: string): (string | CredentialReference)[] {
  const parts: (string | CredentialReference)[] = [];
  let last = 0;
  for (const match of text.matchAll(REFERENCE)) {
    const at = match.index;
    if (at > last) parts.push(text.slice(last, at));
    parts.push({
      raw: match[0],
      id: match[1] ? match[1].toLowerCase() : null,
      version: match[2] ? Number(match[2]) : null,
      form: match[3],
    });
    last = at + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** The credential ids `texts` reference, each once, sorted. */
export function referencedIds(texts: (string | null | undefined)[]): string[] {
  const ids = new Set<string>();
  for (const text of texts) {
    if (!text) continue;
    for (const part of splitReferences(text)) {
      if (typeof part !== 'string' && part.id) ids.add(part.id);
    }
  }
  return [...ids].sort();
}

/**
 * `line` with every reference's version left out: two lines equal this way
 * differ only by the version of the same credentials, which is a rotation, not
 * a change of intent.
 */
export function withoutVersions(line: string): string {
  return line.replace(REFERENCE, (_m, id: string | undefined, _v: string | undefined, form: string) =>
    id ? `{{credential:${id.toLowerCase()}@*|${form}}}` : `{{credential:redacted|${form}}}`,
  );
}

/** Whether `before` became `after` only by a credential's newer version. */
export function isRotation(before: string, after: string): boolean {
  return before !== after && withoutVersions(before) === withoutVersions(after);
}
